import { type AxiosError } from 'axios'
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useBoolean, useMountedState } from 'react-use'

import authentication from 'api/authentication'
import { CompanyListForm } from 'components/authentication/CompanyListForm'
import BusyOverlay from 'components/common/BusyOverlay'
import { isChooseCompanyAuthResponse } from 'context/auth-context-utils'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { messageFromResponseError } from 'utils/strings'
import { ms } from 'utils/time/millisecond'

import type { UserAuthCompany } from 'types/api/user/session'

function getStringOrFirstArrayItem(item: string | string[]) {
  return Array.isArray(item) ? item[0] : item
}

export const GoogleCallback: React.FC = () => {
  const { hash, pathname } = useLocation()
  const navigate = useNavigate()
  const { startSession } = useAuth()
  const [isSubmitting, setIsSubmitting] = useBoolean(false)
  const [accessToken, setAccessToken] = useState('')
  const [nextPathname, setNextPathname] = useState('')
  const [companies, setCompanies] = useState<UserAuthCompany[]>([])
  const isMounted = useMountedState()

  const handleError = (err: AxiosError, defaultText: string) => {
    const errorText = messageFromResponseError(err, defaultText)

    toast({
      type: 'error',
      text: errorText,
      time: ms.seconds(10),
      testKey: 'signin'
    })
  }

  // We don't wrap this function in a try/catch because we expect errors
  // to be caught at the caller
  const handleGoogleLogin = useCallback(
    async (companyName?: string) => {
      setIsSubmitting(true)

      const authResponse = await authentication.signInWithGoogle({
        accessToken,
        companyName
      })

      // Users who are members of multiple companies will first have to choose which company to authenticate with
      if (isChooseCompanyAuthResponse(authResponse)) {
        setIsSubmitting(false)
        setCompanies(authResponse.companies)

        return
      }

      await startSession(authResponse)

      if (nextPathname !== pathname) {
        navigate(nextPathname || '/')
      }
    },
    [
      accessToken,
      pathname,
      nextPathname,
      navigate,
      setIsSubmitting,
      startSession
    ]
  )

  const handleLogin = async (companyName: string) => {
    try {
      await handleGoogleLogin(companyName)
    } catch (err) {
      setIsSubmitting(false)
      handleError(err, 'Unable to authenticate using Google.')
    }
  }

  const handleBack = () => {
    navigate('/')
  }

  useEffect(() => {
    try {
      const params = new URLSearchParams(hash.replace('#', '?'))
      const token = params.get('access_token')
      const state = params.get('state')

      if (!token) {
        throw new Error('Missing token or state from redirect hash')
      }

      const accessToken = getStringOrFirstArrayItem(token)
      const googleState = state ? getStringOrFirstArrayItem(state) : undefined

      setAccessToken((token) => accessToken ?? token)

      // If there's a path to forward to after sign in, grab it from the URL
      if (googleState?.includes('"nextPathname"')) {
        setNextPathname(() => JSON.parse(googleState).nextPathname)
      }
    } catch (err) {
      setIsSubmitting(false)
      handleError(err, 'Unable to authenticate using Google.')
      navigate('/')
    }
  }, [hash, navigate, setIsSubmitting])

  useEffect(() => {
    // When the component "mounts", we make an initial request to the Gremlin API
    // to get a Gremlin credential using Google's access token. If the result is
    // a list of companies, we set the state and wait until the user selects a
    // company at which point we will invoke handleGoogleLogin again.
    const handleMount = async () => {
      if (!accessToken || !isMounted()) {
        return
      }

      try {
        await handleGoogleLogin()
      } catch (err) {
        setIsSubmitting(false)
        handleError(err, 'Unable to authenticate using Google.')
        navigate('/')
      }
    }

    handleMount()
  }, [
    accessToken,
    handleGoogleLogin,
    isMounted,
    navigate,
    setIsSubmitting,
    setAccessToken,
    setNextPathname
  ])

  if (companies?.length) {
    return (
      <CompanyListForm
        companies={companies}
        handleBack={handleBack}
        handleCompanySelection={(companyName) => handleLogin(companyName)}
        submitting={isSubmitting}
      />
    )
  }

  return <BusyOverlay shown text="Authorizing" />
}
